import React, {Fragment, useReducer, useEffect, useCallback} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import Paper from "@mui/material/Paper";
import NumberFormat from "react-number-format";
import ReactModal from "react-modal";

import ai from "../../helpers/axios";
import LeadDetailsDesktop, { SortableTableHead} from "./lead-details-desktop";
import LeadDetailsMobile from "./lead-details-mobile";
import TextAreaInput from "../textarea-input";
import OperatorAutocomplete from "../opi/operator-autocomplete";
import MessageContainer from "../messages/message-container";
import SingleLeadQuotes from "./single-lead-quotes";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment-timezone";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";
import LeadHeader from "./lead-header";

function OperatorCallLogRow(props) {
  let {row, handleDelete} = props;
  const [open, setOpen] = React.useState(false);

  if (!row) return null;

  return (
    <>
      <TableRow
        sx={{"& > *": {borderBottom: "unset"}}}
        onClick={() => setOpen(!open)}
        className={`fm-l2-parent-table-row operator-call-log-row `}
        key={row._id}
      >
        <TableCell key="operatorName">{row?.operator?.name}</TableCell>

        <TableCell key="timestamp">
          {moment(row.timestamp).format("M/D/YY - h:mm a")}
        </TableCell>

        <TableCell key="notes">{row?.notes || "-"}</TableCell>

        <TableCell key="record-actions">
          <div className="d-flex justify-content-end">
            <button
              key={`handle-delete-${row?._id}`}
              onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row._id);
                }
              }
              className="d-block base-button icon-button icon-button-small btn-danger ml-2"
              title="Delete Operator"
            >
              <span className="material-icons">delete</span>
            </button>
          </div>
        </TableCell>
      </TableRow>
    </>
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        lead: {
          ...state.lead,
          [action.field]: action.value,
        },
      };
    }

    case "rootFieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "resetOperatorModal": {
      return {
        ...state,
        selectedOperator: null,
        unavailableOperatorNotes: "",
      };
    }

    case "resetAndCloseOperatorModal": {
      return {
        ...state,
        selectedOperator: null,
        unavailableOperatorNotes: "",
        createOperatorCallLogRecordModalIsOpen: false,
      };
    }

    case "closeOperatorModal": {
      return {
        ...state,
        createOperatorCallLogRecordModalIsOpen: false,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        // loading: true,
      };
    }
    case "dataDownloaded": {
      let sourcingRows = [];
      let lead = action?.payload?.data;

      if (action?.payload?.data?.sourcingRecords) {
        sourcingRows = action?.payload?.data?.sourcingRecords;
      }

      if (!action?.payload?.data?.operatorCallLog?.length) {
        lead.operatorCallLog = [];
      }

      return {
        ...state,
        lead: action.payload.data,
        initialValues: {
          internalComments: action?.payload?.data?.internalComments,
        },
        sourcingRows,
        loading: false,
        loadCount: state.loadCount + 1,
      };
    }

    case "initialFieldChanged": {
      return {
        ...state,
        initialValues: {
          ...state.initialValues,
          [action.field]: action.value,
        }
      }
    }

    case "rowSelectionEvent": {
      let selectedRowIds = [...state.selectedRows].map((row) => row._id);

      let newSelection = new Set([...selectedRowIds]);

      if (action.selected) {
        newSelection.add(action.rowId);
      }

      if (!action.selected) {
        newSelection.delete(action.rowId);
      }

      newSelection = [...newSelection];

      let selectedRows = state?.lead?.sourcingRecords?.filter((record) =>
        newSelection.includes(record._id)
      );

      return { ...state, selectedRows: [...selectedRows] };

      // return {
      //   ...state,
      //   selectedRows: [...selectedRows],
      // };
    }

    case "openPassthroughModal": {
      return {
        ...state,
        passthroughModalOpen: true,
      };
    }

    case "closePassthroughModal": {
      return {
        ...state,
        passthroughModalOpen: false,
        selectedPassthroughOperator: null,
        passthroughNotes: "",
        expectedCommission: "",
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.section];
      } else {
        updatedUnsavedChanges[action.section] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }

    default:
      break;
  }
  return state;
};

const SingleLeadContent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const tab = searchParams.get("tab");
  const messagesTab = searchParams.get("messagesTab");

  const initialState = {
    lead: {
      itineraryDetails: {
        departureAirportName: "",
        arrivalAirportName: "",
        departureDate: "",
        departureTime: "",
        returnDate: "",
        returnTime: "",
        passengerCount: "",
        companyName: "",
      },
      aircraftSelection: [],
      baggage: [],
      quotes: [],
      internalComments: "",
      tripOptions: [],
      operatorCallLog: [],
    },
    initialValues: {
      internalComments: ""
    },

    loading: true,

    selectedRows: [],
    sourcingRows: [],

    deleteSourcingRecordModalOpen: false,
    sourcingRecordToDelete: null,
    deleteOperatorCallLogModalOpen: false,
    operatorCallLogRecordToDelete: null,
    createOperatorCallLogRecordModalIsOpen: false,

    selectedOperator: null,
    unavailableOperatorNotes: "",

    sourcingSortField: "createdAt",
    sourcingSortDirection: -1,
    unavailableOperatorSortDirection: -1,
    unavailableOperatorSortField: "timestamp",
    loadCount: 0,
    latestMessage: null,

    passthroughModalOpen: false,
    selectedPassthroughOperator: null,
    passthroughNotes: "",
    expectedCommission: "",

    unsavedChanges: {},
  };

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdmin = userRoles.some(function (role) {
    return "Super Admin" === role;
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (tab && (tab === "messages" || state?.lead?._id)) {
      setTimeout(() => {
        document
          .getElementById(`${messagesTab}-lead-messages`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });

      }, 300)
    }
  }, [
    tab,
    messagesTab,
    state?.lead?._id,
  ]);

  const formatCurrency = (x) => {
    return "$" + x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const setUnsavedChanges = useCallback(({section, value}) => {
    dispatch({
      type: "unsavedChanges",
      section,
      value
    })
  }, []);

  useEffect(() => {
    setUnsavedChanges({
      section: 'operatorCallLogRecordModal',
      value: state.createOperatorCallLogRecordModalIsOpen,
    });
  }, [setUnsavedChanges, state.createOperatorCallLogRecordModalIsOpen]);

  useEffect(() => {
    setUnsavedChanges({
      section: 'passthroughModal',
      value: state.passthroughModalOpen,
    });
  }, [setUnsavedChanges, state.passthroughModalOpen]);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/leads/${id}/l2`)
      .catch((error) => {
        history.push({ pathname: "/leads" });
        setTimeout(() => {
          toast.error("Unauthorized!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    if (response) {
      console.log("LEAD DATA", response.data);

      dispatch({
        type: "dataDownloaded",
        payload: {
          data: response.data,
        },
      });
    }
  };

  const handleLeadNotesUpdate = async (e) => {
    e.preventDefault();
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai
      .auth(token)
      .put(`/api/leads/${id}/updateNotes`, {
        notes: state.lead.internalComments,
      })
      .catch((e) => console.error(e));

    if (data) {
      setUnsavedChanges({
        section: "internalComments",
        value: false,
      });

      dispatch({
        type: "initialFieldChanged",
        field: "internalComments",
        value: state.lead.internalComments,
      });
    }

    toast.success("Notes Updated.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const handleTextInputChange = (e) => {
    let value = e.target.value;

    dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value,
    });

    setUnsavedChanges({
      section: "internalComments",
      value: value !== state.initialValues.internalComments
    })
  };

  const handleRootTextInputChange = (e) => {
    let value = e.target.value;

    dispatch({
      type: "rootFieldChanged",
      field: e.target.name,
      value: value,
    });
  };

  const handleSourcingRecordDelete = (recordId) => {
    console.log(`DELETE OF ${recordId} INITIATED - MODAL CONFIRMATION`);
    dispatch({
      type: "rootFieldChanged",
      field: "deleteSourcingRecordModalOpen",
      value: true,
    });

    dispatch({
      type: "rootFieldChanged",
      field: "sourcingRecordToDelete",
      value: recordId,
    });
  };

  const handleOperatorCallLogDelete = (recordId) => {
    console.log(
      `DELETE OF OPERATOR CALL LOG ${recordId} INITIATED - MODAL CONFIRMATION`
    );
    dispatch({
      type: "rootFieldChanged",
      field: "deleteOperatorCallLogModalOpen",
      value: true,
    });

    dispatch({
      type: "rootFieldChanged",
      field: "operatorCallLogRecordToDelete",
      value: recordId,
    });
  };

  const handleSourcingRecordHide = async (recordId) => {
    console.log(`HIDE OF ${recordId} INITIATED - API REQUEST`);
    if (!recordId) {
      return;
    }

    // console.log("TO DO", recordId);

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai
      .auth(token)
      .put(`/api/sourcing/${recordId}/toggleHiddenState`)
      .catch((e) => console.error(e));

    if (data) {
      toast.success("Record Updated.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      console.log("UPDATED RECORD", data);
      return getData();
    }

    return toast.error("Record update failed.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const deleteSourcingRecord = async (recordId) => {
    if (!recordId) {
      return;
    }

    console.log(`DELETE OF ${recordId} INITIATED - API REQUEST`);

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    await ai
      .auth(token)
      .delete(`/api/sourcing/${state.sourcingRecordToDelete}`)
      .catch((e) => console.error(e));

    toast.success("Record Deleted.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    getData();
  };

  const deleteOperatorCallLogRecord = async (recordId) => {
    if (!recordId) {
      return;
    }

    console.log(
      `DELETE OF OPERATOR CALL LOG ${recordId} INITIATED - API REQUEST`
    );

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    await ai
      .auth(token)
      .delete(`/api/leads/${id}/operatorCallLog/${recordId}`)
      .catch((e) => console.error(e));

    toast.success("Unavailable Operator Record Deleted.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    getData();
  };

  const handleAddOperatorButton = (e) => {
    console.log("ADD OPERATOR BUTTON");
    dispatch({
      type: "rootFieldChanged",
      field: "createOperatorCallLogRecordModalIsOpen",
      value: true,
    });
  };

  const handleOperatorChange = (selectedOperator) => {
    if (selectedOperator) {
      dispatch({
        type: "rootFieldChanged",
        field: "selectedOperator",
        value: selectedOperator,
      });
    } else {
      resetSelection("selectedOperator");
    }
  };

  const resetSelection = (fieldName) => {
    setTimeout(() => {
      dispatch({
        type: "rootFieldChanged",
        field: fieldName,
        value: null,
      });
    }, 0);
  };

  const addOperatorCallLogRecord = async (e) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (!state?.selectedOperator?._id) {
        toast.error(
          "Please fill in all required fields and retry.",
        );
        return;
    }

    let operatorCallLogData = {
      operator: {
        id: state?.selectedOperator?._id,
        name: state?.selectedOperator?.value,
      },
      notes: state.unavailableOperatorNotes,
    };

    await ai
      .auth(token)
      .put(`/api/leads/${id}/operatorCallLog`, operatorCallLogData)
      .catch((e) => console.error(e));

    toast.success("Unavailable Operator Added.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // dispatch({
    //   type:"fieldChanged",
    // })

    if (e.target.name === "addOperatorCallLog-another") {
      dispatch({ type: "resetOperatorModal" });
    }

    if (e.target.name === "addOperatorCallLog-close") {
      dispatch({ type: "resetAndCloseOperatorModal" });
    }

    getData();
  };

  useEffect(() => {
    getData();
    document.title = "Lead | evoJets HQ";
  }, []);

  useEffect(() => {
    let sourcingRows = [...state.sourcingRows];

    sourcingRows = sourcingRows.sort((a, b) => {
      console.log(
        "SOURCING SORT DATA",
        state.sourcingSortDirection,
        state.sourcingSortField
      );

      if (
        state.sourcingSortField === "createdAt" ||
        state.sourcingSortField === "quoteStatus"
      ) {
        if (state.sourcingSortDirection === 1) {
          if (a?.hidden === false && b?.hidden === false) {
            return (
              new Date(a[state.sourcingSortField]) -
              new Date(b[state.sourcingSortField])
            );
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          return (
            new Date(a[state.sourcingSortField]) -
            new Date(b[state.sourcingSortField])
          );
        }

        if (state.sourcingSortDirection === -1) {
          console.log("HIDDEN", a.hidden, b.hidden);

          if (a?.hidden === false && b?.hidden === false) {
            return (
              new Date(b[state.sourcingSortField]) -
              new Date(a[state.sourcingSortField])
            );
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          return (
            new Date(b[state.sourcingSortField]) -
            new Date(a[state.sourcingSortField])
          );
        }
        // return false;
      }

      if (
        state.sourcingSortField === "aircraftName" ||
        state.sourcingSortField === "operatorName"
      ) {
        console.log(a[state.sourcingSortField], b[state.sourcingSortField]);
        if (state.sourcingSortDirection === 1) {
          if (a?.hidden === false && b?.hidden === false) {
            return a[state.sourcingSortField]?.localeCompare(
              b[state.sourcingSortField]
            );
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          return a[state.sourcingSortField]?.localeCompare(
            b[state.sourcingSortField]
          );
        }

        if (state.sourcingSortDirection === -1) {
          if (a?.hidden === false && b?.hidden === false) {
            return b[state.sourcingSortField]?.localeCompare(
              a[state.sourcingSortField]
            );
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }
          return b[state.sourcingSortField]?.localeCompare(
            a[state.sourcingSortField]
          );
        }
        // return false;
      }

      if (
        state.sourcingSortField === "operatorPrice" ||
        state.sourcingSortField === "totalClientPayment"
      ) {
        if (state.sourcingSortDirection === 1) {
          if (a?.hidden === false && b?.hidden === false) {
            return a[state.sourcingSortField] - b[state.sourcingSortField];
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          return a[state.sourcingSortField] - b[state.sourcingSortField];
        }

        if (state.sourcingSortDirection === -1) {
          if (a?.hidden === false && b?.hidden === false) {
            return b[state.sourcingSortField] - a[state.sourcingSortField];
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          if (a?.hidden === true) {
            return 1;
          }

          if (b?.hidden === true) {
            return -1;
          }

          return b[state.sourcingSortField] - a[state.sourcingSortField];
        }
        // return false;
      }

      return;
    });

    dispatch({
      type: "rootFieldChanged",
      field: "sourcingRows",
      value: sourcingRows,
    });

    console.log("SOURCING ROWS SORTED", sourcingRows);
  }, [state.sourcingSortDirection, state.sourcingSortField, state.loading]);

  useEffect(() => {
    let unavailableOperators = [...state?.lead?.operatorCallLog];

    unavailableOperators = unavailableOperators.sort((a, b) => {
      console.log(
        "UNAVAILABLE SORT DATA",
        state.unavailableOperatorSortDirection,
        state.unavailableOperatorSortField
      );

      if (state.unavailableOperatorSortField === "timestamp") {
        if (state.unavailableOperatorSortDirection === 1) {
          return (
            new Date(a[state.unavailableOperatorSortField]) -
            new Date(b[state.unavailableOperatorSortField])
          );
        }

        if (state.unavailableOperatorSortDirection === -1) {
          return (
            new Date(b[state.unavailableOperatorSortField]) -
            new Date(a[state.unavailableOperatorSortField])
          );
        }
        // return false;
      }

      if (state.unavailableOperatorSortField === "operatorName") {
        if (state.unavailableOperatorSortDirection === 1) {
          return a.operator.name.localeCompare(b.operator.name);
        }

        if (state.unavailableOperatorSortDirection === -1) {
          return b.operator.name.localeCompare(a.operator.name);
        }
        // return false;
      }

      return;
    });

    dispatch({
      type: "fieldChanged",
      field: "operatorCallLog",
      value: unavailableOperators,
    });

    console.log("UNAVAILABLE ROWS SORTED", unavailableOperators);
  }, [
    state.unavailableOperatorSortDirection,
    state.unavailableOperatorSortField,
    state.loadCount,
  ]);

  const handleSourcingSort = (field) => {
    console.log("SOURCING SORT", field);

    if (field === state.sourcingSortField) {
      //toggle direction only
      return dispatch({
        type: "rootFieldChanged",
        field: "sourcingSortDirection",
        value: state.sourcingSortDirection === -1 ? 1 : -1,
      });
    }

    dispatch({
      type: "rootFieldChanged",
      field: "sourcingSortField",
      value: field,
    });
    dispatch({
      type: "rootFieldChanged",
      field: "sourcingSortDirection",
      value: -1,
    });
  };

  const handleUnavailableOperatorSort = (field) => {
    console.log("UNAVAILABLE SORT", field);

    if (field === state.unavailableOperatorSortField) {
      //toggle direction only
      return dispatch({
        type: "rootFieldChanged",
        field: "unavailableOperatorSortDirection",
        value: state.unavailableOperatorSortDirection === -1 ? 1 : -1,
      });
    }

    dispatch({
      type: "rootFieldChanged",
      field: "unavailableOperatorSortField",
      value: field,
    });
    dispatch({
      type: "rootFieldChanged",
      field: "unavailableOperatorSortDirection",
      value: -1,
    });
  };

  const handlePassthroughSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      await ai.auth(token).patch(`/api/leads/${id}/pt`, {
        operator: state.selectedPassthroughOperator,
        notes: state.passthroughNotes,
        expectedCommission: state.expectedCommission,
      });

      toast.success("Lead converted to passthrough status");
      dispatch({ type: "closePassthroughModal" });
      getData(); // Refresh lead data
    } catch (error) {
      toast.error("Failed to convert lead to passthrough");
      console.error(error);
    }
  };

  return (
    <Fragment>
      {!state.loading && (
        <LeadHeader
          lead={state.lead}
        />
      )}
      {!state.loading && state?.lead?.isPassthrough && (
        <div className="passthrough-badge mt-20">
          <span className="badge bg-success">Passthrough</span>
          <div className="mt-10">
            <strong>Operator:</strong>{" "}
            {state?.lead?.passthroughData?.operator?.label || "N/A"}
            {" | "}
            <strong>Expected Commission:</strong>{" "}
            {state?.lead?.passthroughData?.expectedCommission
              ? formatCurrency(state?.lead?.passthroughData?.expectedCommission)
              : "N/A"}
          </div>
        </div>
      )}

      {!state.loading && state.lead?.itineraryDetails?.isClickToFlyEligible && state.lead?.clickToFlyResult && (
        <div className="ctf-eligible-section">
          <div className="ctf-eligible-header">
            {state.lead.clickToFlyResult.noticeRequiredDays > 0 && state.lead.itineraryDetails?.departureDate && (
              <div className="ctf-eligible-until">
                Click to Fly pricing available through{" "}
                {moment(state.lead.itineraryDetails.departureDate)
                  .subtract(state.lead.clickToFlyResult.noticeRequiredDays, "days")
                  .format("MMMM Do, YYYY")}
              </div>
            )}

            {(() => {
              const ctfR = state.lead.clickToFlyResult;
              const policies = ctfR.cancellationPolicy?.policies;
              if (!policies?.length) return null;
              const depDate = new Date(state.lead.itineraryDetails?.departureDate);
              const condensed = policies.map((p) => {
                if (/upon signing/i.test(p)) {
                  const m = p.match(/^(\d+%)/);
                  return m ? `${m[1]} upon signing` : p;
                }
                const dm = p.match(/on or (?:before|after)(?:\s+9am ET on)?\s+(\w+ \d+\w*, \d{4})/);
                if (dm) {
                  const pd = new Date(dm[1].replace(/(\d+)\w+,/, "$1,"));
                  const diffMs = depDate - pd;
                  const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
                  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                  const pm = p.match(/^(\d+%)/);
                  const pct = pm ? pm[1] : "";
                  const timeStr = diffHrs <= 72 ? `${diffHrs}hrs` : `${diffDays} days`;
                  return `${pct} within ${timeStr}`;
                }
                return p;
              }).join(" / ");
              const tripTypeLabel = ctfR.lowestPriceMethod?.toUpperCase() || "N/A";
              return (
                <div className="ctf-cancellation-terms">
                  <strong>Pricing Method:</strong> {tripTypeLabel} &nbsp;&middot;&nbsp; <strong>Cancellation:</strong> {condensed}
                </div>
              );
            })()}
          </div>

          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Commonly Available</TableCell>
                  <TableCell className="ctf-th-price">Price</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell className="ctf-th-wifi">Price w/ WiFi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.lead.clickToFlyResult.aircraftOptions?.map((ac, acIdx) => {
                  const yomOptions = ac.toggleOptions?.yom?.options;
                  if (!yomOptions?.length) {
                    return (
                      <TableRow key={`ctf-ac-${acIdx}`} className={acIdx > 0 ? "ctf-aircraft-group-start" : undefined}>
                        <TableCell>{ac.aircraftTypeName}</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell className="ctf-cell-price ctf-price-base">{formatCurrency(Math.round(ac.defaultPrice))}</TableCell>
                        <TableCell className="ctf-type-base">Base</TableCell>
                        <TableCell className="ctf-cell-wifi">—</TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <Fragment key={`ctf-ac-${acIdx}`}>
                      {yomOptions.map((yom, yomIdx) => {
                        const changeDollars = yom.change?.dollars || 0;
                        const price = Math.round(ac.defaultPrice + changeDollars);
                        let type = "Base";
                        if (yom.isDiscount) type = "Discount";
                        else if (yom.isIncrease) type = "Premium";
                        const wifiAvailable = yom.wifi?.available && yom.wifi.addOnDollars != null;
                        const wifiTotal = wifiAvailable ? formatCurrency(Math.round(price + yom.wifi.addOnDollars)) : null;
                        const wifiAddOn = wifiAvailable ? `+${formatCurrency(Math.round(yom.wifi.addOnDollars))}` : null;
                        return (
                          <TableRow
                            key={`ctf-ac-${acIdx}-yom-${yomIdx}`}
                            className={yomIdx === 0 && acIdx > 0 ? "ctf-aircraft-group-start" : undefined}
                          >
                            {yomIdx === 0 && (
                              <TableCell rowSpan={yomOptions.length}>
                                {ac.aircraftTypeName}
                              </TableCell>
                            )}
                            <TableCell className={`ctf-cell-${type.toLowerCase()}`}>{yom.year}</TableCell>
                            <TableCell className={`ctf-cell-${type.toLowerCase()}`}>{yom.commonlyAvailable || "—"}</TableCell>
                            <TableCell className={`ctf-cell-${type.toLowerCase()} ctf-cell-price ctf-price-${type.toLowerCase()}`}>{formatCurrency(price)}</TableCell>
                            <TableCell className={`ctf-cell-${type.toLowerCase()} ctf-type-${type.toLowerCase()}`}>{type}</TableCell>
                            <TableCell className="ctf-cell-wifi">
                              {wifiAvailable ? (
                                <Tooltip
                                  title={<><span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>WiFi add-on</span><br /><span style={{ fontWeight: 600, fontSize: 13, letterSpacing: "0.3px" }}>{wifiAddOn}</span></>}
                                  arrow
                                  placement="top"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "#0f172a",
                                        color: "#fff",
                                        fontSize: 12,
                                        fontFamily: "inherit",
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: "6px",
                                        textAlign: "center",
                                        lineHeight: 1.5,
                                        "& .MuiTooltip-arrow": { color: "#0f172a" },
                                      },
                                    },
                                  }}
                                >
                                  <span style={{ cursor: "default", borderBottom: "1px dashed rgba(100, 116, 139, 0.4)", paddingBottom: 1 }}>{wifiTotal}</span>
                                </Tooltip>
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="ctf-view-full-link">
            <Link to={`/click-to-fly/results/${state.lead.clickToFlyResult._id}`}>
              View full pricing breakdown &rarr;
            </Link>
          </div>
        </div>
      )}

      {!state.loading && (
        <MessageContainer
          className="compact-messages-view"
          id="lead-messages"
          recordId={id}
          recordType="lead"
          superAdmin={superAdmin}
          setUnsavedChanges={setUnsavedChanges}
        />
      )}

      {!state.loading && !isMobile && (
        <LeadDetailsDesktop
          superAdmin={superAdmin}
          rows={[state.lead]}
          lead={state.lead}
          sourcingRows={state.sourcingRows}
          handleSourcingRecordDelete={handleSourcingRecordDelete}
          handleSourcingRecordHide={handleSourcingRecordHide}
          parentDispatch={dispatch}
          setUnsavedChanges={setUnsavedChanges}
          selectedRows={state.selectedRows}
          handleSourcingSort={handleSourcingSort}
          sourcingSortField={state.sourcingSortField}
          sourcingSortDirection={state.sourcingSortDirection}
        />
      )}

      {!state.loading && isMobile && (
        <LeadDetailsMobile
          rows={[state.lead]}
          lead={state.lead}
          sourcingRows={state.sourcingRows}
          handleSourcingRecordDelete={handleSourcingRecordDelete}
          handleSourcingRecordHide={handleSourcingRecordHide}
          parentDispatch={dispatch}
          setUnsavedChanges={setUnsavedChanges}
          selectedRows={state.selectedRows}
          handleSourcingSort={handleSourcingSort}
        />
      )}

      {!state.loading && <SingleLeadQuotes lead={state.lead} />}

      {!state.loading && (
        <div className="two-column-grid">
          <div>
            <h5 className="mt-30 mb-1">Lead Notes</h5>

            <Paper className="compact-notes-view">
              <form className="base-form" onSubmit={handleLeadNotesUpdate}>
                <h4 className="lead-notes-heading compact-view">Lead Notes</h4>
                <TextAreaInput
                  type="text"
                  name="internalComments"
                  value={state.lead.internalComments}
                  label="Lead Notes"
                  placeholder={`Add a Note`}
                  handleChange={handleTextInputChange}
                  className="lead-internal-notes"
                  containerClasses="lead-l2-notes-textarea-container"
                  hideLabel
                ></TextAreaInput>
                <div className="p-l-16 p-r-16 p-b-16">
                  <input type="submit" value="Save Notes"></input>
                </div>
              </form>
            </Paper>
          </div>

          <div>
            <h5 className="mt-30 mb-1 d-flex">
              Unavailable Operators

              <Box className="base-button icon-button icon-button-small evo-dark-green ml-2"
                   onClick={handleAddOperatorButton} title="Add Operator">
                <span className="material-icons">add_circle</span>
              </Box>
            </h5>

            <TableContainer
              component={Paper}
              className="mt-0 accordion-table-with-details shadow-none"
              key="operatorCallLogTable"
            >
              <Table aria-label="collapsible table" className="table-compact-view">
                <TableHead className="fm-l2-parent-table-head">
                  <TableRow className="fm-l2-parent-table-head-row">
                    <SortableTableHead
                      onClick={handleUnavailableOperatorSort}
                      id="operatorName"
                      label="OPERATOR"
                      sortDirection={state.unavailableOperatorSortDirection}
                      sortField={state.unavailableOperatorSortField}
                    />

                    <SortableTableHead
                      onClick={handleUnavailableOperatorSort}
                      id="timestamp"
                      label="TIMESTAMP"
                      sortDirection={state.unavailableOperatorSortDirection}
                      sortField={state.unavailableOperatorSortField}
                    />

                    <TableCell>NOTES</TableCell>
                    <TableCell>
                      <div className="d-flex justify-content-end">ACTIONS</div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="fm-l2-parent-table-body">
                  {state.lead?.operatorCallLog?.length > 0 ?
                    state.lead?.operatorCallLog.map((row) => (
                      <OperatorCallLogRow
                        key={row?._id}
                        row={row}
                        leadData={state.lead}
                        handleDelete={handleOperatorCallLogDelete}
                        dispatch={dispatch}
                      />
                    )) :
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography className="mt-10 mb-10">There are no records to display</Typography>
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      )}

      {!state.loading && (
        <div className="mt-20">
          <Link
            className="evo-blue base-button"
            to={{ pathname: `/contracts/new`, state: { lead: state.lead } }}
          >
            Create Contract From Lead
          </Link>
        </div>
      )}

      {!state.loading && !state.lead.isPassthrough && (
        <div className="mt-20">
          <button
            className="evo-blue base-button text-center"
            onClick={() => dispatch({ type: "openPassthroughModal" })}
          >
            Convert to Passthrough
          </button>
        </div>
      )}
      <ReactModal
        isOpen={state.createOperatorCallLogRecordModalIsOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          dispatch({
            type: "rootFieldChanged",
            field: "createOperatorCallLogRecordModalIsOpen",
            value: false,
          });
        }}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            // height: "700px",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <h3 className="text-center mb-20">Add an Unavailable Operator</h3>

        <form
          className="base-form mb-20 w-100"
          id="sourcingModuleForm"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <OperatorAutocomplete
            handleChange={handleOperatorChange}
            defaultOperatorOptions={state.operatorOptions}
            parentDispatch={dispatch}
            selectedOperator={state.selectedOperator}
            required
          />
          <TextAreaInput
            type="text"
            name="unavailableOperatorNotes"
            value={state.unavailableOperatorNotes}
            label="Notes"
            placeholder={`Add a Note`}
            handleChange={handleRootTextInputChange}
            className="lead-internal-notes"
          />

          <button
            className="mt-0 base-button evo-blue fg-1 p-12 w-100"
            id="addOperatorCallLog-another"
            type="submit"
            name="addOperatorCallLog-another"
            onClick={addOperatorCallLogRecord}
          >
            Submit &amp; Add Another
          </button>

          <button
            className="mt-20 base-button evo-gunmetal fg-1 p-12 w-100"
            id="addOperatorCallLog-close"
            type="submit"
            name="addOperatorCallLog-close"
            onClick={addOperatorCallLogRecord}
          >
            Submit &amp; Close
          </button>

          <button
            className="mt-20 base-button btn-danger fg-1 p-12 w-100"
            type="button"
            onClick={() => {
              dispatch({
                type: "rootFieldChanged",
                field: "createOperatorCallLogRecordModalIsOpen",
                value: false,
              });
            }}
          >
            Cancel
          </button>
        </form>
      </ReactModal>
      <ReactModal
        isOpen={state.deleteOperatorCallLogModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          dispatch({
            type: "rootFieldChanged",
            field: "deleteOperatorCallLogModalOpen",
            value: false,
          });

          dispatch({
            type: "rootFieldChanged",
            field: "operatorCallLogRecordToDelete",
            value: null,
          });
        }}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            // height: "700px",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <h3>Are you sure you want to delete this record?</h3>
        <div id="confirmationButtons">
          <button
            onClick={(e) => {
              e.preventDefault();
              dispatch({
                type: "rootFieldChanged",
                field: "deleteOperatorCallLogModalOpen",
                value: false,
              });
            }}
            className="base-button evo-blue"
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              dispatch({
                type: "rootFieldChanged",
                field: "deleteOperatorCallLogModalOpen",
                value: false,
              });

              deleteOperatorCallLogRecord(state.operatorCallLogRecordToDelete);
            }}
            className="base-button btn-danger delete-button-shadow"
          >
            Yes, Delete
          </button>
        </div>
      </ReactModal>
      <ReactModal
        isOpen={state.deleteSourcingRecordModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          dispatch({
            type: "rootFieldChanged",
            field: "deleteSourcingRecordModalOpen",
            value: false,
          });

          dispatch({
            type: "rootFieldChanged",
            field: "sourcingRecordToDelete",
            value: null,
          });
        }}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            // height: "700px",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <h3>Are you sure you want to delete this record?</h3>
        <div id="confirmationButtons">
          <button
            onClick={(e) => {
              e.preventDefault();
              dispatch({
                type: "rootFieldChanged",
                field: "deleteSourcingRecordModalOpen",
                value: false,
              });
            }}
            className="base-button evo-blue"
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              dispatch({
                type: "rootFieldChanged",
                field: "deleteSourcingRecordModalOpen",
                value: false,
              });

              deleteSourcingRecord(state.sourcingRecordToDelete);
            }}
            className="base-button btn-danger delete-button-shadow"
          >
            Yes, Delete
          </button>
        </div>
      </ReactModal>
      {/* passthrough modal */}
      <ReactModal
        isOpen={state.passthroughModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          dispatch({
            type: "closePassthroughModal",
          });
        }}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <h3 className="text-center mb-20">Convert Lead to Passthrough</h3>

        <form
          className="base-form mb-20 w-100"
          id="passthroughForm"
          onSubmit={handlePassthroughSubmit}
        >
          <OperatorAutocomplete
            handleChange={(op) =>
              dispatch({
                type: "rootFieldChanged",
                field: "selectedPassthroughOperator",
                value: op,
              })
            }
            defaultOperatorOptions={state.operatorOptions}
            parentDispatch={dispatch}
            selectedOperator={state.selectedPassthroughOperator}
            required
          />

          <TextAreaInput
            type="text"
            name="passthroughNotes"
            value={state.passthroughNotes}
            label="Notes"
            placeholder="Add notes"
            handleChange={handleRootTextInputChange}
            required
          />

          <div className="form-group">
            <label>Expected Commission</label>
            <NumberFormat
              required
              value={state.expectedCommission}
              thousandSeparator={true}
              prefix={"$"}
              onValueChange={(values) => {
                dispatch({
                  type: "rootFieldChanged",
                  field: "expectedCommission",
                  value: values.value,
                });
              }}
              placeholder="Expected Commission"
              className="semibold-text"
              decimalScale={2}
            />
          </div>

          <button
            className="mt-20 base-button evo-blue fg-1 p-12 w-100"
            type="submit"
          >
            Convert to Passthrough
          </button>

          <button
            className="mt-20 base-button btn-danger p-12 w-100"
            type="button"
            onClick={() => {
              dispatch({
                type: "closePassthroughModal",
              });
            }}
          >
            Cancel
          </button>
        </form>
      </ReactModal>
      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>
    </Fragment>
  );
};

export default SingleLeadContent;
